import { CORPUS } from './chorales/corpusIndex';
import { TUNE_NOTES } from './chorales/editorial';
import { FEELINGS, PATHS, PIECES } from './index';
import { keyName, parseKey } from '../music/key';

/**
 * One search over everything in the atlas: pieces, tunes, patterns, feelings and pages. Each
 * item has the words it can be found by; a query matches when every word of it appears.
 */

export type SearchKind = 'piece' | 'tune' | 'pattern' | 'feeling' | 'page';

export interface SearchItem {
  kind: SearchKind;
  title: string;
  meta: string;
  href: string;
  /** Every word the item can be found by, folded, each preceded by a space (so " w" finds a word start). */
  words: string;
  /** The home key of a piece, folded ("b minor"), to rank pieces in the key a query names. */
  key?: string;
}

export const fold = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[♯#]/g, ' sharp ').replace(/[♭]/g, ' flat ').toLowerCase();
/** Folded words, split at anything that is not a letter or digit ("BWV 244.15" → bwv 244 15). */
const tokens = (s: string) => fold(s).split(/[^a-z0-9]+/).filter(Boolean);
const wordsOf = (s: string) => ` ${tokens(s).join(' ')}`;
const keyOf = (token: string) => tokens(keyName(parseKey(token))).join(' ');

const PAGES: Array<[string, string, string, string]> = [
  ['Pieces', 'Every piece in the atlas, searchable', '#library', 'library list all browse'],
  ['The 24 keys', 'The Well-Tempered Clavier, Book I: every opening in order', '#keys', 'well tempered clavier preludes keys tour wtc'],
  ['Compare', 'The same music heard twice', '#compare', 'comparison rooms'],
  ['One melody, five harmonies', 'The Passion Chorale in the St Matthew Passion', '#passion', 'passion chorale herzlich tut mich verlangen o haupt'],
  ['One tune, many harmonies', 'Every tune Bach set more than once', '#tunes', 'tunes hymns settings'],
  ['Theme and variations', 'Every variation lined up under its theme', '#variations', 'variations mozart beethoven twinkle'],
  ['When the music returns', 'Sonata themes, the first time and when they return', '#forms', 'exposition recapitulation sonata form rondo refrain'],
  ['Patterns', 'Cadences, sequences and stock progressions', '#paths', 'paths schemata cadences sequences'],
  ['Listen by feeling', 'Start from what you hear', '#feel', 'feelings perceptual index'],
  ['Statistics', 'What the composers do, counted', '#observatory', 'observatory counts numbers charts'],
  ['Play chords', 'An instrument on the gravity well', '#walk', 'walk play chords instrument midi'],
  ['Train your ear', 'Listening questions made from real music', '#train', 'ear training quiz exercises practice'],
  ['About', 'How to use the atlas and how it is computed', '#method', 'method help how to use about tension'],
];

let items: SearchItem[] | null = null;

export function searchItems(): SearchItem[] {
  if (items) return items;
  const out: SearchItem[] = [];
  for (const p of Object.values(PIECES)) {
    out.push({
      kind: 'piece',
      title: p.title,
      meta: `${p.composer.split(' ').pop()} · ${p.catalogue}`,
      href: `#${p.id}`,
      words: wordsOf(`${p.title} ${p.catalogue} ${p.composer} ${p.collection} ${keyName(parseKey(p.homeKey))}`),
      key: keyOf(p.homeKey),
    });
  }
  for (const e of CORPUS.entries) {
    const composer = e.composer ?? (e.kind === 'sonata' ? 'Mozart' : 'Bach');
    out.push({
      kind: 'piece',
      title: e.title,
      meta: `${composer} · ${e.catalogue} · ${keyName(parseKey(e.homeKey))}`,
      href: `#${e.id}`,
      words: wordsOf(`${e.title} ${e.catalogue} ${composer} ${e.collection} ${e.tune} ${e.subtitle} ${keyName(parseKey(e.homeKey))}`),
      key: keyOf(e.homeKey),
    });
  }
  for (const t of CORPUS.tunes) {
    const name = TUNE_NOTES[t.slug]?.name ?? t.name;
    out.push({ kind: 'tune', title: name, meta: `A tune Bach set ${t.ids.length} times`, href: `#tune.${t.slug}`, words: wordsOf(`${name} ${t.name} tune hymn`) });
  }
  for (const p of PATHS) {
    out.push({ kind: 'pattern', title: p.name, meta: p.feeling, href: `#path.${p.id}`, words: wordsOf(`${p.name} ${p.feeling} ${p.family} pattern`) });
  }
  for (const f of FEELINGS) {
    out.push({ kind: 'feeling', title: f.phrase, meta: f.word, href: `#feel.${f.id}`, words: wordsOf(`${f.phrase} ${f.word} feeling`) });
  }
  for (const [title, meta, href, words] of PAGES) out.push({ kind: 'page', title, meta, href, words: wordsOf(`${title} ${meta} ${words}`) });
  items = out;
  return out;
}

const KEY_IN_QUERY = /(?:^| )([a-g])(?: (sharp|flat))? (major|minor)(?= |$)/;

/**
 * Items where every word of the query starts a word of the item ("pass" finds Passion, "in" does not
 * find Twinkle), best first: the title starting with the query, then query words starting words of
 * the title, then of the line under it; a piece in the key the query names ("prelude in b minor")
 * is found, pieces in other keys are not.
 */
export function search(query: string, limit = 40): SearchItem[] {
  const words = tokens(query);
  if (!words.length) return [];
  const q = words.join(' ');
  const key = q.match(KEY_IN_QUERY);
  const wantKey = key ? [key[1], key[2], key[3]].filter(Boolean).join(' ') : null;
  const scored: Array<[number, SearchItem]> = [];
  for (const it of searchItems()) {
    if (!words.every((w) => it.words.includes(` ${w}`))) continue;
    const t = wordsOf(it.title);
    // A query naming a key ("prelude in b minor") leaves out pieces in other keys, unless the title names it (a sonata's middle movement).
    if (wantKey && it.key && it.key !== wantKey && !t.includes(` ${wantKey}`)) continue;
    const m = wordsOf(it.meta);
    let score = 0;
    if (t.startsWith(` ${q}`)) score += 100;
    else if (t.includes(` ${q}`)) score += 60;
    for (const w of words) {
      if (t.includes(` ${w} `) || t.endsWith(` ${w}`)) score += 12;
      else if (t.includes(` ${w}`)) score += 8;
      else if (m.includes(` ${w}`)) score += 3;
    }
    if (wantKey && it.key === wantKey) score += 40;
    score += { page: 8, pattern: 6, feeling: 5, tune: 4, piece: 0 }[it.kind];
    scored.push([score, it]);
  }
  return scored
    .sort((a, b) => b[0] - a[0] || a[1].title.localeCompare(b[1].title))
    .slice(0, limit)
    .map(([, it]) => it);
}
